import React, { useEffect, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import moment from 'moment';
import { CustomerWorkoutCreateFormData } from '../beans/customer.workout.create.form.data';
import {
    CustomerWorkoutCreateUpdate,
    resetCustomerWorkoutFormValue,
    resetWorkoutItem,
    setCustomerItem,
    setCustomerWorkoutFormValue,
    setWorkoutItem,
    setWorkoutSetItems,
} from '../state/customer.slice';
import { FormManager } from 'src/_shared/managers/form.manager';
import { NavigationManager } from 'src/_shared/managers/navigation.manager';
import { PopupManager } from 'src/_shared/managers/popup.manager';
import { QueueManager, QueueKeyType } from 'src/_shared/managers/queue.manager';
import { WorkoutManager } from 'src/_shared/managers/workout.manager';
import { WorkoutSessionManager } from 'src/_shared/managers/workout.session.manager';
import { WorkoutSessionSetManager } from 'src/_shared/managers/workout.session.set.manager';
import { WorkoutSessionSetTransitManager } from 'src/_shared/managers/workout.session.set.transit.manager';
import { WorkoutSetManager } from 'src/_shared/managers/workout.set.manager';
import { RouteEnum } from 'src/_shared/routes';
import { store } from 'src/_shared/store/configure';
import { useStoreSelector } from 'src/_shared/store/hook';
import { User, Workout, SessionCreateRequest, Session, ProgramSessionSetCreateRequest, ProgramSessionSetStatusEnum, SessionTitleUpdate, SetTransitByExerciseRequest } from 'src/api/entities';
import { PopupType } from 'src/features/_shared/components/Popup/popup.item';
import { TextField } from 'src/features/_shared/components/TextField';
import { setWorkoutDashboardItems, setWorkoutItems } from 'src/features/workout/store/workout.slice';
import { Button } from 'src/features/_shared/components/Button/button.component';
import { Calendar } from 'src/features/_shared/components/DatePicker/datepicker.component';
import { CustomerWorkoutSessionCreateSetFormData } from '../beans/customer.workout.session.create.form.data';
import 'react-datepicker/dist/react-datepicker.css';
import { setSelectedWorkoutItem, WorkoutSessionCreateUpdate } from 'src/features/workout/store/workout.session.slice';
import { DashboardManager } from 'src/_shared/managers/dashboard.manager';
import { UserManager } from 'src/_shared/managers/user.manager';
import { PageTransition } from 'src/features/_shared/components/PageTransition/page.transition.component';
import { EmptyState } from 'src/features/_shared/components/EmptyState/empty.state.component';

export const CustomerWorkoutsView: React.FC = () => {
    const workoutManager = WorkoutManager.getInstance();
    const workoutSessionManager = WorkoutSessionManager.getInstance();
    const dashboardManager = DashboardManager.getInstance();
    const workoutSessionSetManager = WorkoutSessionSetManager.getInstance();
    const workoutSessionSetTransitManager = WorkoutSessionSetTransitManager.getInstance();
    const navigationManager = NavigationManager.getInstance();
    const customerManager = UserManager.getInstance();

    const queueManager = QueueManager.getInstance();
    const notificationManager = PopupManager.getInstance();
    const mavigationManager = NavigationManager.getInstance();
    const user: User = useStoreSelector(state => state.reducer.accessSlice.loggedUser);
    const workoutSetManager = WorkoutSetManager.getInstance();
    const customerItem: User = useStoreSelector(state => state.reducer.customerSlice.customer);
    const workouts: Workout[] = useStoreSelector(state => state.reducer.workoutSlice.workouts);
    const workoutsDashboard: Workout[] = useStoreSelector(state => state.reducer.workoutSlice.workoutsDashboard);
    const workoutCreateUpdate: CustomerWorkoutCreateUpdate = useStoreSelector(state => state.reducer.customerSlice.workoutCreateUpdate);
    const workoutItem: Workout = useStoreSelector(state => state.reducer.customerSlice.workout);
    const workoutSessionCreateUpdate: WorkoutSessionCreateUpdate = useStoreSelector(state => state.reducer.workoutSessionSlice.workoutSessionCreateUpdate);

    const getItems = async () => {
        const value = (await workoutManager.search(customerItem.id)).data!;
        store.dispatch(setWorkoutItems({ value }));
    };

    const getWorkoutHeadStatus = async () => {
        const value = await dashboardManager.getWorkoutHeadStatus({
            authorID: customerItem.id,
        });
        store.dispatch(setWorkoutDashboardItems({ value }));
    };

    useEffect(() => {
        getItems();
        getWorkoutHeadStatus();
    }, []);

    const handleCreateWorkout = async () => {
        const request: SessionCreateRequest = {
            data: {
                author: {
                    id: user.id!,
                    name: user.username,
                },
                assignee: {
                    id: customerItem.id!,
                    name: customerItem.data?.details?.firstName ?? '' + customerItem.data?.details?.lastName,
                },
                details: {
                    title: workoutCreateFormData.getValues('name'),
                },
            },
        };
        const response = await queueManager.queue(QueueKeyType.DEFAULT, workoutManager.create(request));
        store.dispatch(setWorkoutItem({ response }));

        const getItems = async () => {
            const value = (await workoutManager.search(customerItem.id)).data!;
            store.dispatch(setWorkoutItems({ value }));
            if (value.length > 0) {
                const sets = (await workoutSetManager.search(response.id!)).data;
                if (sets != undefined) {
                    store.dispatch(setWorkoutSetItems({ sets }));
                }
            }
        };
        await getItems();
        await getWorkoutHeadStatus();
    };

    const workoutCreateFormData = FormManager.getInstance().buildFormProxy<CustomerWorkoutCreateFormData>(
        CustomerWorkoutCreateFormData,
        handleCreateWorkout,
        {
            name: workoutCreateUpdate.name,
        },
        'onChange',
    );

    const [customerWorkoutSessionDateState, setCustomerWorkoutSessionDateState] = useState(new Date());

    const workoutSessionCreateFormData = FormManager.getInstance().buildFormProxy<CustomerWorkoutSessionCreateSetFormData>(
        CustomerWorkoutSessionCreateSetFormData,
        handleCreateWorkout,
        {
            sessionDate: workoutSessionCreateUpdate.sessionDate,
        },
        'onChange',
    );

    const handleOnBlur = (key: any, value: string) => {
        store.dispatch(setCustomerWorkoutFormValue({ key, value }));
    };

    const getWorkoutData = async (workoutID: number) => {
        const response = await workoutManager.getById(workoutID);
        store.dispatch(resetWorkoutItem());
        store.dispatch(setWorkoutItem({ response }));
    };
    const handleWorkoutDetail = async (workoutID: number) => {
        await getWorkoutData(workoutID);
        mavigationManager.navigateTo(RouteEnum.customersWorkoutsCreate);
    };

    const handleDownloadSheet = async (workoutID: number) => {
        const blob = await workoutSetManager.createSessionSetSheet(0, 100, undefined, undefined, { sessionID: workoutID });
        
        // Create a temporary URL from the blob
        const url = window.URL.createObjectURL(blob);
        
        // Create a temporary anchor element to trigger download
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `workout_${workoutID}.xls`);
        
        // Trigger the download
        document.body.appendChild(link);
        link.click();
        
        // Clean up
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        toast.success('Scheda scaricata con successo!');
    };
    const handleWorkoutRename = async (selectedWorkout: Session) => {
        workoutCreateFormData.setValue('name', selectedWorkout.title);
        notificationManager.show({
            type: PopupType.Info,
            title: 'Rinomina protocollo',
            icon: 'bi-clipboard2-pulse-fill',
            children: (
                <div className='d-flex mb-2'>
                    <div className='col'>
                        <TextField
                            type='text'
                            fullWidth
                            placeholder='Nome protocollo'
                            id='name'
                            {...workoutCreateFormData.register('name')}
                            error={workoutCreateFormData.errors.name}
                            errorMessage={workoutCreateFormData.errors.name && 'Campo obbligatorio'}
                            onBlur={(e: React.FocusEvent<HTMLInputElement, Element>) => {
                                workoutCreateFormData.setValue('name', e.target.value);
                            }}
                        />
                    </div>
                </div>
            ),
            ctas: {
                primary: {
                    cta: async () => {
                        const updateRequest: SessionTitleUpdate = {
                            title: workoutCreateFormData.getValues('name'),
                        };
                        await workoutManager.rename(selectedWorkout.id!, updateRequest);
                        await getItems();
                    },
                },
                close: {
                    cta: () => {
                        workoutCreateFormData.resetField('name');
                    },
                },
            },
        });
    };

    const handleDeleteWorkout = async (workoutID: number) => {
        await workoutManager.delete(workoutID);
        await getItems();
    };

    const handleWorkoutArchive = async (workoutID: number) => {
        await workoutManager.archive(workoutID, true);
        await getItems();
        toast('Sessione archiviata con successo!');
    };

    const handleInspectSessions = async (workoutID: number) => {
        const response = await workoutManager.getById(workoutID);
        store.dispatch(setWorkoutItem({ response }));
        store.dispatch(setSelectedWorkoutItem({ value: response }));
        mavigationManager.navigateTo(RouteEnum.workoutSessions);

         workoutSessionCreateUpdate.selectedWorkout.id
    };

    const handleCreateWorkoutSession = async (workout: Session) => {
        const response = await workoutSessionManager.create({
            status: 'DONE',
            data: {
                author: {
                    id: workout.assigneeID,
                    name: workout.assigneeName,
                },
                details: {
                    id: workout.id!,
                    name: workout.title,
                    date: workoutSessionCreateFormData.getValues('sessionDate'),
                },
            },
        });

        if (response != undefined) {
            const sets = (await workoutSetManager.search(workout.id!)).data;
            sets?.forEach(async currentSet => {
                const requestSet: ProgramSessionSetCreateRequest = {
                    authorId: workout.assigneeID,
                    authorName: workout.assigneeName,
                    sessionID: response.id!,
                    sessionName: response.sessionName,
                    setDate: response.sessionDate,
                    status: ProgramSessionSetStatusEnum.Done,
                    data: {
                        details: {
                            exercise: {
                                group: currentSet.exerciseGroup,
                                uuid: currentSet.exerciseUuid,
                                name: currentSet.exerciseName,
                            },
                            date: response.sessionDate,
                            rest: currentSet.rest,
                            rir: currentSet.rir,
                            rpe: currentSet.rpe,
                            reps: currentSet.reps,
                            weight: currentSet.weight,
                        },
                    },
                };
                await workoutSessionSetManager.create(response.id!, requestSet);
            });
        }

        const transitRequest: SetTransitByExerciseRequest = {
            sessionID: response.id,
            authorID: customerItem.id!,
        };
        workoutSessionSetTransitManager.transitBySession(transitRequest);
        await getWorkoutHeadStatus();
    };

    const handleInspectAnalysis = async (customerID: number) => {
        const value: User = await queueManager.queue(QueueKeyType.DEFAULT, customerManager.getUserById(customerID));
        store.dispatch(setCustomerItem({ value }));
        navigationManager.navigateTo(RouteEnum.customersAnalysis);
    };
    const handleRedirectArchived = async () => {
        mavigationManager.navigateTo(RouteEnum.customersWorkoutsArchived);
    };

    const handleStartCreate = () => {
        notificationManager.show({
            type: PopupType.Info,
            title: 'Crea protocollo',
            icon: 'bi-clipboard2-pulse-fill',
            maxWidth: 'xl',
            children: (
                <div className='d-flex mb-2'>
                    <div className='col'>
                        <TextField
                            type='text'
                            fullWidth
                            placeholder='Nome protocollo'
                            id='name'
                            {...workoutCreateFormData.register('name')}
                            error={workoutCreateFormData.errors.name}
                            errorMessage={workoutCreateFormData.errors.name && 'Campo obbligatorio'}
                            onBlur={(e: React.FocusEvent<HTMLInputElement, Element>) => handleOnBlur(e.target.name, e.target.value)}
                        />
                    </div>
                </div>
            ),
            ctas: {
                primary: {
                    cta: () => {
                        handleCreateWorkout(), 
                        mavigationManager.navigateTo(RouteEnum.customersWorkoutsCreate), 
                        notificationManager.hide(),
                        store.dispatch(resetCustomerWorkoutFormValue());
                    },
                },
                close: {
                    cta: () => {
                        store.dispatch(resetCustomerWorkoutFormValue());
                    },
                },
            },
        });
    };
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [startSessionCreate, setStartSessionCreate] = useState(new Boolean(false));
    const [selectedWorkout, setSelectedWorkout] = useState({});

    moment.locale('it');

    const cardColors = ['#ff4949', '#D93955', '#e06040', '#c74a72', '#b05ca6', '#7b68c9', '#4a8fd9'];

    const getCardColor = (workout: Session) => {
        const id = workout.id ?? 0;
        return cardColors[id % cardColors.length];
    };

    const getProgressPercent = (workout: Session): number | null => {
        const dashboard = workoutsDashboard?.find(w => w.id === workout.id);
        if (!dashboard || !dashboard.progress) return null;
        return Math.round(dashboard.progress);
    };

    return (
        <PageTransition className='mt-4 px-3 px-md-4'>
            <Toaster />

            {/* ── Page Header ──────────────────────────────────────────── */}
            <div
                className='d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 p-4 anim-slide-up'
                style={{
                    background: 'linear-gradient(135deg, #fff 0%, #fafafa 100%)',
                    borderRadius: 12,
                    border: '1px solid rgba(0,0,0,0.06)',
                }}
            >
                <div>
                    <div className='d-flex align-items-center gap-2 mb-1'>
                        <i className='bi bi-clipboard2-pulse-fill' style={{ fontSize: '1.4rem', color: '#ff4949' }}></i>
                        <h4 className='mb-0 fw-bold' style={{ color: '#333' }}>Protocolli</h4>
                    </div>
                    <p className='mb-0 mt-1' style={{ color: '#666', fontSize: '0.9rem' }}>
                        <span style={{ color: '#888' }}>
                            <i className='bi bi-person me-1' style={{ fontSize: '0.8rem' }}></i>
                            {customerItem.data?.details?.firstName} {customerItem.data?.details?.lastName}
                        </span>
                        {workouts.length > 0 && (
                            <span className='badge rounded-pill ms-2' style={{ backgroundColor: '#ff4949', color: '#fff', fontSize: '0.8rem' }}>
                                {workouts.length}
                            </span>
                        )}
                    </p>
                </div>
                <div className='d-flex flex-wrap gap-2 mt-3 mt-md-0'>
                    <Button
                        identifier='Nuovo protocollo'
                        icon={<i className='bi bi-plus-lg' style={{ fontSize: '1rem', color: 'white' }}></i>}
                        variant='primary'
                        onClick={handleStartCreate}
                    />
                    <Button
                        identifier='Analisi'
                        icon={<i className='bi bi-bar-chart-line-fill' style={{ fontSize: '1rem', color: 'white' }}></i>}
                        variant='primary'
                        onClick={() => handleInspectAnalysis(customerItem.id!)}
                    />
                    <Button
                        identifier='Archiviati'
                        icon={<i className='bi bi-archive-fill' style={{ fontSize: '1rem' }}></i>}
                        variant='secondary'
                        onClick={() => handleRedirectArchived()}
                    />
                </div>
            </div>

            {/* ── Session Create Panel ─────────────────────────────────── */}
            <div
                className={`session-create-panel mb-4 ${startSessionCreate == true ? 'session-create-panel--open' : ''}`}
                style={{ backgroundColor: '#f9f9f9', borderRadius: 10, border: '1px solid rgba(0,0,0,0.06)' }}
            >
                {startSessionCreate == true && (
                    <div className='d-flex flex-column flex-md-row align-items-center justify-content-between p-4 gap-3'>
                        <div className='d-flex align-items-center gap-3 flex-grow-1'>
                            <div
                                style={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '50%',
                                    backgroundColor: '#ff4949',
                                    color: '#fff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0,
                                }}
                            >
                                <i className='bi bi-calendar-plus' style={{ fontSize: '1rem' }}></i>
                            </div>
                            <div>
                                <h6 className='mb-1 fw-semibold' style={{ color: '#333' }}>Nuova seduta</h6>
                                <span style={{ color: '#888', fontSize: '0.82rem' }}>Seleziona la data per creare una seduta</span>
                            </div>
                        </div>
                        <div className='d-flex align-items-center gap-3'>
                            <Calendar
                                identifier={''}
                                {...workoutSessionCreateFormData.register('sessionDate')}
                                selected={
                                    workoutSessionCreateFormData.getValues('sessionDate') == ''
                                        ? moment(new Date()).toDate()
                                        : moment(workoutSessionCreateFormData.getValues('sessionDate')).toDate()
                                }
                                onChange={date => {
                                    setSelectedDate(date as Date);
                                    workoutSessionCreateFormData.setValue('sessionDate', date as Date);
                                }}
                            />
                        </div>
                        <div className='d-flex gap-2'>
                            <Button
                                identifier='Crea seduta'
                                icon={<i className='bi bi-check-lg' style={{ fontSize: '1rem', color: 'white' }}></i>}
                                variant='primary'
                                onClick={() => {
                                    handleCreateWorkoutSession(selectedWorkout);
                                    setStartSessionCreate(false);
                                }}
                            />
                            <Button
                                identifier='Annulla'
                                variant='secondary'
                                onClick={() => {
                                    setStartSessionCreate(false);
                                    workoutCreateFormData.resetField('sessionDate');
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>

            {/* ── Workout List ─────────────────────────────────────────── */}
            {workouts.length > 0 ? (
                <>
                    <div className='d-flex align-items-center gap-2 mb-3 anim-slide-up'>
                        <i className='bi bi-list-ul' style={{ fontSize: '1.1rem', color: '#ff4949' }}></i>
                        <h6 className='mb-0 fw-semibold' style={{ color: '#333' }}>Elenco protocolli</h6>
                        <span className='badge rounded-pill' style={{ backgroundColor: '#f2f2f2', color: '#666', fontSize: '0.75rem' }}>
                            {workouts.length}
                        </span>
                    </div>
                    <div className='d-flex flex-column gap-3 mb-4'>
                        {workouts.map((workout: Session, index: number) => {
                            const dashboard = workoutsDashboard?.find(w => w.id === workout.id);
                            const progress = getProgressPercent(workout);

                            return (
                                <div
                                    className='app-card anim-stagger'
                                    key={workout.id || index}
                                    style={{
                                        '--anim-index': index,
                                        borderLeft: `3px solid ${getCardColor(workout)}`,
                                        padding: 0,
                                        overflow: 'hidden',
                                    } as React.CSSProperties}
                                >
                                    <div className='d-flex flex-column flex-lg-row align-items-lg-center justify-content-between p-3'>
                                        {/* ── Left: Icon + Info ─── */}
                                        <div className='d-flex align-items-start gap-3 flex-grow-1'>
                                            {/* Icon circle */}
                                            <div
                                                style={{
                                                    width: 48,
                                                    height: 48,
                                                    borderRadius: '50%',
                                                    backgroundColor: getCardColor(workout),
                                                    color: '#fff',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    flexShrink: 0,
                                                    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                                                }}
                                            >
                                                <i className='bi bi-clipboard2-pulse-fill' style={{ fontSize: '1.1rem' }}></i>
                                            </div>

                                            {/* Info */}
                                            <div className='d-flex flex-column' style={{ minWidth: 0 }}>
                                                <div className='d-flex align-items-center gap-2 flex-wrap'>
                                                    <h6 className='mb-0 fw-semibold' style={{ color: '#222', fontSize: '1rem' }}>
                                                        {workout.title}
                                                    </h6>
                                                </div>

                                                {workout.createdAt && (
                                                    <span style={{ color: '#aaa', fontSize: '0.78rem', marginTop: 2 }}>
                                                        <i className='bi bi-calendar3 me-1' style={{ fontSize: '0.7rem' }}></i>
                                                        Creato il {moment(workout.createdAt).format('D MMM YYYY')}
                                                    </span>
                                                )}

                                                {/* Stats badges */}
                                                {dashboard && (
                                                    <div className='d-flex flex-wrap gap-2 mt-2'>
                                                        {dashboard.sessionsDone !== undefined && (
                                                            <span className='badge rounded-pill' style={{ backgroundColor: '#e6f9ee', color: '#1a7f42', fontSize: '0.72rem', fontWeight: 500 }}>
                                                                <i className='bi bi-check-circle me-1' style={{ fontSize: '0.65rem' }}></i>
                                                                {dashboard.sessionsDone} sedute
                                                            </span>
                                                        )}
                                                        {dashboard.groupCount !== undefined && (
                                                            <span className='badge rounded-pill' style={{ backgroundColor: '#f2f2f2', color: '#333', fontSize: '0.72rem', fontWeight: 500 }}>
                                                                <i className='bi bi-collection me-1' style={{ fontSize: '0.65rem' }}></i>
                                                                {dashboard.groupCount} gruppi
                                                            </span>
                                                        )}
                                                        {dashboard.exerciseCount !== undefined && (
                                                            <span className='badge rounded-pill' style={{ backgroundColor: '#f2f2f2', color: '#333', fontSize: '0.72rem', fontWeight: 500 }}>
                                                                <i className='bi bi-activity me-1' style={{ fontSize: '0.65rem' }}></i>
                                                                {dashboard.exerciseCount} esercizi
                                                            </span>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* ── Right: Actions ─── */}
                                        <div className='d-flex align-items-center gap-2 mt-3 mt-lg-0 ms-lg-3 flex-shrink-0 flex-wrap'>
                                            
                                            <Button
                                                icon={<i className='bi bi-file-earmark-excel-fill' style={{ fontSize: '1rem', color: 'white' }}></i>}
                                                identifier='Scarica'
                                                variant='primary'
                                                onClick={() => handleDownloadSheet(workout.id!)}
                                            />
                                            <Button
                                                icon={<i className='bi bi-eye-fill' style={{ fontSize: '1rem', color: 'white' }}></i>}
                                                identifier='Dettagli'
                                                variant='primary'
                                                onClick={() => handleWorkoutDetail(workout.id!)}
                                            />
                                            <Button
                                                icon={<i className='bi bi-cursor-text' style={{ fontSize: '1rem', color: 'white' }}></i>}
                                                identifier='Rinomina'
                                                variant='primary'
                                                onClick={() => handleWorkoutRename(workout)}
                                            />
                                            <Button
                                                icon={<i className='bi bi-calendar-check-fill' style={{ fontSize: '1rem', color: 'white' }}></i>}
                                                identifier='Sedute'
                                                variant='primary'
                                                onClick={() => handleInspectSessions(workout.id!)}
                                            />
                                            <Button
                                                icon={<i className='bi bi-plus-circle-fill' style={{ fontSize: '1rem', color: 'white' }}></i>}
                                                identifier='Crea seduta'
                                                variant='primary'
                                                onClick={() => {
                                                    setStartSessionCreate(!startSessionCreate);
                                                    setSelectedWorkout(workout);
                                                }}
                                            />
                                            <Button
                                                icon={<i className='bi bi-archive-fill' style={{ fontSize: '1rem' }}></i>}
                                                variant='secondary'
                                                onClick={() => handleWorkoutArchive(workout.id!)}
                                            />
                                            <Button
                                                icon={<i className='bi bi-trash3-fill' style={{ fontSize: '1rem' }}></i>}
                                                variant='secondary'
                                                onClick={() => handleDeleteWorkout(workout.id!)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            ) : (
                <EmptyState
                    icon='bi bi-clipboard2-pulse'
                    title='Nessun protocollo'
                    description='Non ci sono ancora protocolli per questo cliente. Crea il primo protocollo.'
                    action={<Button identifier='Nuovo protocollo' onClick={handleStartCreate} variant='primary' />}
                />
            )}
        </PageTransition>
    );
};
